(function () {
  'use strict';

  var DB_NAME = 'justpress-local-assets-v1';
  var STORE_NAME = 'assets';
  var DB_VERSION = 1;
  var objectUrls = new Map();
  var memoryRecords = new Map();
  var previewRecords = new Map();
  var PREVIEW_PREFIX = 'justpress-local-preview-v1:';

  function report(error, context) {
    if (window.JPRuntime && typeof window.JPRuntime.reportError === 'function') window.JPRuntime.reportError(error, context);
  }

  function withDeadline(promise, milliseconds, message) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timer = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        reject(new Error(message || 'Local draft storage timed out.'));
      }, milliseconds);
      Promise.resolve(promise).then(function (value) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(value);
      }, function (error) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        reject(error);
      });
    });
  }

  function previewKey(assetId) { return PREVIEW_PREFIX + String(assetId || ''); }

  function readPreview(assetId) {
    if (previewRecords.has(assetId)) return previewRecords.get(assetId);
    try {
      var value = sessionStorage.getItem(previewKey(assetId)) || '';
      if (value) previewRecords.set(assetId, value);
      return value;
    } catch (_) { return ''; }
  }

  function writePreview(assetId, value) {
    if (!assetId || !value) return;
    previewRecords.set(assetId, value);
    try { sessionStorage.setItem(previewKey(assetId), value); } catch (error) { report(error, 'local-preview-session-write'); }
  }

  function createPreview(blob) {
    return withDeadline(new Promise(function (resolve) {
      if (!blob || !/^image\/(png|jpeg)$/i.test(blob.type || '')) return resolve('');
      var reader = new FileReader();
      reader.onerror = function () { resolve(''); };
      reader.onload = function () {
        var source = String(reader.result || '');
        var image = new Image();
        image.onerror = function () { resolve(blob.size <= 1800000 ? source : ''); };
        image.onload = function () {
          try {
            var scale = Math.min(1, 1400 / Math.max(image.naturalWidth || 1, image.naturalHeight || 1));
            var canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
            canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
            var context = canvas.getContext('2d');
            context.fillStyle = '#fff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.drawImage(image, 0, 0, canvas.width, canvas.height);
            var preview = canvas.toDataURL('image/jpeg', .84);
            if (preview.length > 1500000) {
              var compactScale = Math.min(1, 900 / Math.max(image.naturalWidth || 1, image.naturalHeight || 1));
              canvas.width = Math.max(1, Math.round(image.naturalWidth * compactScale));
              canvas.height = Math.max(1, Math.round(image.naturalHeight * compactScale));
              context = canvas.getContext('2d');
              context.fillStyle = '#fff';
              context.fillRect(0, 0, canvas.width, canvas.height);
              context.drawImage(image, 0, 0, canvas.width, canvas.height);
              preview = canvas.toDataURL('image/jpeg', .76);
            }
            resolve(preview);
          } catch (_) { resolve(blob.size <= 1800000 ? source : ''); }
        };
        image.src = source;
      };
      reader.readAsDataURL(blob);
    }), 6000, 'Local preview generation timed out.').catch(function () { return ''; });
  }

  function openDb() {
    return withDeadline(new Promise(function (resolve, reject) {
      if (!window.indexedDB) return reject(new Error('Local draft storage is unavailable in this browser.'));
      var request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = function () {
        var db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      };
      request.onsuccess = function () { resolve(request.result); };
      request.onerror = function () { reject(request.error || new Error('Local draft storage could not be opened.')); };
      request.onblocked = function () { reject(new Error('Local draft storage is blocked by another tab.')); };
    }), 5000, 'Local draft storage took too long to open.');
  }

  function transaction(mode, callback) {
    return openDb().then(function (db) {
      return withDeadline(new Promise(function (resolve, reject) {
        var tx = db.transaction(STORE_NAME, mode);
        var store = tx.objectStore(STORE_NAME);
        var result;
        try { result = callback(store); } catch (error) { reject(error); return; }
        tx.oncomplete = function () { resolve(result); db.close(); };
        tx.onerror = function () { reject(tx.error || new Error('Local draft storage failed.')); db.close(); };
        tx.onabort = function () { reject(tx.error || new Error('Local draft storage was interrupted.')); db.close(); };
      }), 7000, 'Local draft storage took too long to finish.').catch(function (error) {
        try { db.close(); } catch (closeError) { report(closeError, 'local-assets-db-close'); }
        throw error;
      });
    });
  }

  function id(prefix) {
    var bytes = new Uint8Array(8);
    if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes);
    else for (var index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
    return (prefix || 'asset') + '-' + Date.now().toString(36) + '-' + Array.from(bytes).map(function (value) { return value.toString(16).padStart(2, '0'); }).join('');
  }

  function put(blob, options) {
    options = options || {};
    if (!(blob instanceof Blob)) return Promise.reject(new Error('No valid local artwork was provided.'));
    var assetId = options.id || id(options.prefix || 'asset');
    var record = {
      id: assetId,
      blob: blob,
      name: options.name || blob.name || 'justpress-artwork',
      type: blob.type || options.type || 'application/octet-stream',
      kind: options.kind || 'source',
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    /* Keep a same-document copy even when IndexedDB reports success. Some
       browsers can later suspend or deny the database while an upload request
       or file chooser is active. */
    memoryRecords.set(assetId, record);
    var previewReady = (options.kind === 'source' || options.kind === 'render') ? createPreview(blob).then(function (value) { writePreview(assetId, value); return value; }) : Promise.resolve('');
    return previewReady.then(function () {
      return transaction('readwrite', function (store) { store.put(record); return assetId; });
    }).catch(function (error) {
      /* Shopify's theme editor can block IndexedDB inside its preview iframe.
         A compact session preview is the only safe reload fallback in that
         environment. Never report a memory-only record as durable. */
      if (readPreview(assetId)) return assetId;
      memoryRecords.delete(assetId);
      throw error;
    });
  }

  function get(assetId) {
    if (memoryRecords.has(assetId)) return Promise.resolve(memoryRecords.get(assetId));
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(STORE_NAME, 'readonly');
        var request = tx.objectStore(STORE_NAME).get(assetId);
        request.onsuccess = function () { resolve(request.result || null); db.close(); };
        request.onerror = function () { reject(request.error || new Error('Local artwork could not be read.')); db.close(); };
      });
    }).catch(function () { return memoryRecords.get(assetId) || null; });
  }

  function file(assetId) {
    return get(assetId).then(function (record) {
      if (!record || !record.blob) {
        var preview = readPreview(assetId);
        if (!preview) throw new Error('The local artwork is no longer available. Choose the file again.');
        return fetch(preview).then(function (response) { return response.blob(); }).then(function (blob) {
          return new File([blob], 'justpress-recovered-artwork.jpg', { type: blob.type || 'image/jpeg' });
        });
      }
      return new File([record.blob], record.name || 'justpress-artwork', { type: record.type || record.blob.type || 'application/octet-stream' });
    });
  }

  function url(assetId) {
    if (!assetId) return Promise.resolve('');
    if (objectUrls.has(assetId)) return Promise.resolve(objectUrls.get(assetId));
    return get(assetId).then(function (record) {
      if (!record || !record.blob) return readPreview(assetId);
      var value = URL.createObjectURL(record.blob);
      objectUrls.set(assetId, value);
      return value;
    }).catch(function () { return readPreview(assetId); });
  }

  function remove(assetId) {
    if (!assetId) return Promise.resolve();
    memoryRecords.delete(assetId);
    previewRecords.delete(assetId);
    try { sessionStorage.removeItem(previewKey(assetId)); } catch (error) { report(error, 'local-preview-session-remove'); }
    if (objectUrls.has(assetId)) {
      try { URL.revokeObjectURL(objectUrls.get(assetId)); } catch (error) { report(error, 'local-preview-url-revoke'); }
      objectUrls.delete(assetId);
    }
    return transaction('readwrite', function (store) { store.delete(assetId); }).catch(function (error) {
      report(error, 'local-asset-delete');
      return false;
    });
  }

  /* Do not clear memory or revoke URLs on pagehide. pagehide also fires for
     bfcache and native-picker lifecycle transitions in supported browsers; the
     document owns these values and the browser releases them on real unload. */

  window.JPLocalAssets = { put: put, get: get, file: file, url: url, remove: remove, id: id, preview: readPreview };
})();
